import { FC, useEffect, useState } from 'react';
import Table from '@components/table';
import { TCashbackFilter } from '@xeppt/xeppt-sdk/types';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import CashbackStatus from '@components/common/cashback_status';
import { capitalizeFirstLetter, formatDate } from '@utils/index';
import illustration from '@svg/illustrations/empty_cashback.svg';
import styles from './styles.module.scss';

interface IProps {
    search?: string;
    date_from?: Date;
    date_to?: Date;
}

const CashbackTable: FC<IProps> = ({ search, date_to, date_from }) => {
    const [cursor, setCursor] = useState<Date>();
    const [filter, setFilter] = useState<TCashbackFilter>({
        order: 'DESC',
        status: undefined
    });

    useEffect(() => {
        setFilter((state) => ({
            ...state,
            date_to,
            date_from
        }));
        if (date_from) {
            setCursor(date_from);
        }
    }, [date_to, date_from]);

    const columns = [
        {
            label: 'Date & Time',
            key: 'created_at',
            isSort: true,
            render: (val: Date) => (
                <div className={styles.date_wrapper}>
                    <span>{val && formatDate(val, 'MMM DD, YYYY')}</span>
                    <span>{val && formatDate(val, 'HH:mm')}</span>
                </div>
            )
        },
        {
            label: 'Store Name',
            key: 'merchant'
        },
        {
            label: 'Order ID',
            key: 'orderId'
        },
        {
            label: 'Status',
            key: 'status',
            render: (val: any) => (
                <CashbackStatus variant={val}>
                    {capitalizeFirstLetter(val.toLowerCase())}
                </CashbackStatus>
            ),
            filterOptions: [
                { label: 'Pending', value: 'PENDING' },
                { label: 'Confirmed', value: 'CONFIRMED' },
                { label: 'Paid', value: 'PAID' },
                { label: 'Ready', value: 'READY' },
                { label: 'Withdrawn', value: 'REDEEMED' }
            ]
        },
        {
            label: 'Purchase Amount',
            key: 'saleAmount',
            render: (val: number) => `$${val?.toFixed(2)}`
        },
        {
            label: 'Cashback Amount',
            key: 'deviceAmount',
            render: (val: number) => `$${val?.toFixed(2)}`
        }
    ];

    const { data: cashbackTransactions, handleRequest: refetchCashbackTransactions } = useApiQuery({
        method: (data: any) => {
            return apiAccountService.getCashbackList(
                data?.isNew
                    ? {}
                    : {
                          ...data?.filter,
                          status:
                              (data?.filter?.status?.length || 0) > 0
                                  ? data?.filter?.status
                                  : undefined,
                          date_to,
                          date_from
                      },
                {
                    cursor: cursor,
                    limit: 10
                }
            );
        },
        onSuccess: (data, request: any) => {
            if (cursor !== data.cursor && !request?.isNew) {
                setCursor(data.cursor);
            }
        },
        deps: [date_to, date_from, search]
    });

    if (cashbackTransactions && cashbackTransactions?.data?.length === 0) {
        return (
            <div className={styles.empty_table}>
                <img src={illustration} alt="" />
                <div className={styles.title}>No cashback activity yet</div>
                <div className={styles.description}>
                    Once you start earning cashback, the history will appear here
                </div>
            </div>
        );
    }

    return (
        <Table
            columns={columns}
            rows={cashbackTransactions?.data || []}
            emptyDescription="No transactions yet"
            totalRows={cashbackTransactions?.total}
            onLoadData={() => {
                refetchCashbackTransactions({ filter });
            }}
            //@ts-ignore
            filter={{ status: filter.status }}
            //@ts-ignore
            sort={{ key: 'created_at', value: filter.order }}
            onSortChange={(_, value) => {
                setFilter((state) => ({
                    ...state,
                    order: value.toUpperCase() as 'ASC' | 'DESC'
                }));
                setCursor(undefined);
                refetchCashbackTransactions({
                    isNew: false,
                    filter: {
                        ...filter,
                        order: value.toUpperCase() as 'ASC' | 'DESC'
                    }
                });
            }}
            onFilterChange={(val) => {
                setFilter((state) => ({
                    ...state,
                    ...val
                }));
                setCursor(undefined);
                refetchCashbackTransactions({
                    isNew: false,
                    filter: {
                        ...filter,
                        ...val
                    }
                });
            }}
        />
    );
};

export default CashbackTable;
